import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import ExpandedEditorModal from "./ExpandedEditorModal";
import MatplotlibQuickPreview from "./MatplotlibQuickPreview";
import { withHandoutFigureLabels } from "../../features/handoutFigureLabels";
import { getOrRenderMatplotlibPreview } from "../../shared/matplotlibPreviewCache";
import { applyHandoutPythonRedraw, getHandoutImagePythonCode, withHandoutTransparentBackground } from "../../features/handoutImagePython";

// Adapt handout images to the existing Vim editor and preview workspace.
// autoPreviewOnOpen renders the saved code as soon as the dialog opens, so the
// preview pane starts with a loading state instead of the limited quick preview.
export default function HandoutImagePythonDialog({ image, bodyFontSize = 12, previewMatplotlibCode, onApply, onClose, autoPreviewOnOpen = false }) {
  const [code, setCode] = useState(() => getHandoutImagePythonCode(image));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(() => Boolean(autoPreviewOnOpen && previewMatplotlibCode && getHandoutImagePythonCode(image).trim()));
  const [error, setError] = useState("");
  const request = useRef(0);
  const closeAfterRender = useRef(false);
  const currentCode = useRef(code);
  currentCode.current = code;
  // The render for the current draft; editing or closing abandons it.
  const renderAbort = useRef(null);
  const abandonRender = () => { renderAbort.current?.abort(); renderAbort.current = null; };
  useEffect(() => {
    const previous = document.activeElement;
    return () => { request.current += 1; abandonRender(); if (previous?.isConnected) previous.focus(); };
  }, []);
  const changeCode = (value) => {
    closeAfterRender.current = false;
    abandonRender();
    currentCode.current = value; request.current += 1;
    setCode(value); setPreview(null); setBusy(false); setError("");
  };
  const redraw = async () => {
    const source = currentCode.current.trim(); if (!source || !previewMatplotlibCode) return;
    const token = ++request.current;
    abandonRender();
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    renderAbort.current = controller;
    setBusy(true); setPreview(null); setError("");
    try {
      const result = await getOrRenderMatplotlibPreview(withHandoutFigureLabels(withHandoutTransparentBackground(source), image.widthCm || 4, bodyFontSize), previewMatplotlibCode, { signal: controller?.signal });
      if (request.current !== token || currentCode.current.trim() !== source || result?.aborted) return;
      if (!result?.success || !result.image) throw new Error(result?.error || "重畫失敗，請檢查圖碼後再試。");
      const rendered = { code: source, image: result.image };
      setPreview(rendered);
      if (closeAfterRender.current) {
        closeAfterRender.current = false;
        onApply(applyHandoutPythonRedraw(image, source, rendered));
      }
      // The rendered image itself is the feedback; no toast between the two Space+W steps.
    } catch (failure) {
      if (request.current === token) {
        closeAfterRender.current = false;
        setError(failure.message || "重畫失敗，請稍後再試。");
      }
    } finally { if (request.current === token) setBusy(false); }
  };
  // Runs on mount only. A remount (e.g. StrictMode) invalidates the first
  // request in the cleanup above and starts a fresh one; the cache dedupes it.
  useEffect(() => {
    if (autoPreviewOnOpen && currentCode.current.trim() && previewMatplotlibCode) redraw();
    // Only the code present when the dialog opened is rendered automatically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ready = preview && preview.code === code.trim() && !busy;
  const leaderWrite = () => {
    if (busy) closeAfterRender.current = true;
    else if (ready) onApply(applyHandoutPythonRedraw(image, preview.code, preview));
    else { closeAfterRender.current = false; redraw(); }
    return true;
  };
  const previewContent = <Preview aria-label="Python 重畫預覽" aria-busy={busy}>
    {busy ? <Loading role="status"><Spinner aria-hidden="true" />正在重畫圖片…</Loading> : preview ? <img src={preview.image} alt="Python 重畫結果"
      onLoad={(event) => { const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
        if (width > 0 && height > 0) setPreview((value) => value?.code === currentCode.current.trim() ? { ...value, width, height } : value); }} />
      : <MatplotlibQuickPreview transparentBackground code={code} pending compact unsupportedLabel="按 Space + W 產生完整圖片" />}
  </Preview>;
  return <ExpandedEditorModal onClose={onClose} onCopy={(value) => navigator.clipboard?.writeText(value)} editor={{
    key: `handout-image-${image.id}`, title: "Python 圖碼", value: code, onChange: changeCode,
    defaultVimMode: true, syntaxMode: "python", preview: true, previewType: "code",
    previewTitle: "圖片預覽 · 透明背景", previewContent,
    onLeaderWrite: leaderWrite,
    footerContent: <Footer><span>{autoPreviewOnOpen
      ? "透明背景；標籤為題目字級的 70%。開啟時自動重畫；改過圖碼後 Space + W 先重畫，再按一次套用並關閉。"
      : "透明背景；標籤為題目字級的 70%。Space + W 第一次重畫，第二次套用並關閉。"}</span>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      <Actions><button type="button" onClick={onClose}>取消</button>
        <button type="button" disabled={busy || !code.trim() || !previewMatplotlibCode} onClick={redraw}>重畫預覽</button>
        <button type="button" disabled={!ready} onClick={() => onApply(applyHandoutPythonRedraw(image, preview.code, preview))}>套用重畫圖片</button></Actions>
    </Footer>,
  }} />;
}
const Preview = styled.div`min-width: 0; min-height: 180px; display: grid; place-items: center; background-color: var(--app-surface, #fff); background-image: repeating-conic-gradient(#9992 0% 25%, transparent 0% 50%); background-size: 20px 20px; img { max-width: 100%; max-height: min(560px, 45dvh); object-fit: contain; }`;
const Footer = styled.div`min-width: 0; max-height: 28dvh; overflow: auto; flex-shrink: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 10px; font-size: 12px; color: var(--app-muted-text, #64748b);`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-left: auto; button { padding: 8px 12px; border: 1px solid var(--app-border, #ccc); border-radius: 6px; background: var(--app-surface-soft, #f5f5f5); color: var(--app-text, #202124); cursor: pointer; } button:disabled { opacity: .5; cursor: default; }`;
const ErrorMessage = styled.span`color: #b91c1c;`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.span`display: inline-flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 999px; background: var(--app-surface, #fff); color: var(--app-muted-text, #5f6368); font-size: 13px; font-weight: 700; box-shadow: 0 1px 4px rgba(0, 0, 0, .12);`;
const Spinner = styled.i`width: 16px; height: 16px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 999px; animation: ${spin} .7s linear infinite; will-change: transform;`;
